import { localStorageHelper } from "@offstreetparking/design-system/utils";
import { useCallback, useMemo, useSyncExternalStore } from "react";

const LOCAL_STORAGE_CHANGE_EVENT = "local-storage-change";

const notifyChange = (key: string) => {
	window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, { detail: key }));
};

export function useLocalStorage<T>(key: string, initialValue: T) {
	const subscribe = useCallback(
		(onStoreChange: () => void) => {
			const handleStorage = (e: StorageEvent) => {
				if (e.key === key) {
					onStoreChange();
				}
			};
			const handleCustom = (e: Event) => {
				if ((e as CustomEvent).detail === key) {
					onStoreChange();
				}
			};

			window.addEventListener("storage", handleStorage);
			window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleCustom);

			return () => {
				window.removeEventListener("storage", handleStorage);
				window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleCustom);
			};
		},
		[key],
	);

	const getSnapshot = useCallback(() => {
		try {
			return localStorage.getItem(key);
		} catch {
			return null;
		}
	}, [key]);

	const getServerSnapshot = useCallback(() => null, []);

	const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

	const value = useMemo<T>(() => {
		try {
			return raw !== null ? JSON.parse(raw) : initialValue;
		} catch {
			return initialValue;
		}
	}, [raw, initialValue]);

	const setValue = useCallback(
		(newValue: T | ((prev: T) => T)) => {
			const currentValue = localStorageHelper.getItem(key) ?? initialValue;
			const resolved = newValue instanceof Function ? newValue(currentValue) : newValue;
			localStorageHelper.setItem(key, resolved);
			notifyChange(key);
		},
		[key, initialValue],
	);

	const removeValue = useCallback(() => {
		localStorage.removeItem(key);
		notifyChange(key);
	}, [key]);

	return [value, setValue, removeValue] as const;
}
